import { supabase } from './supabase.js'
import { shiftMonthKey, todayISO } from './format.js'

/**
 * Installment plans. He pays his wife's credit card and purchases on it often
 * run in instalments; he wants each tracked on its own, by due date, as 7 of 12.
 *
 * A plan is a default payable that runs out. Every charge is written up front
 * so the schedule is a fact rather than a calculation, which is what lets the
 * app answer "what is due next" with a query instead of arithmetic.
 *
 * The card is a text label. Nothing here reconciles a statement or tracks a
 * card balance — he was explicit about that.
 */

async function logIt(kind, summary, targetId) {
  try {
    await supabase.from('activity_log').insert({
      kind, summary, target_type: 'installment', target_id: targetId ?? null,
    })
  } catch {
    /* never fail the real write */
  }
}

/**
 * Charges for the whole term, written at creation.
 *
 * The last charge absorbs any rounding, so the schedule always sums to the
 * total he typed. A plan that quietly adds ₱0.04 is a plan he stops trusting.
 */
export function buildSchedule({ startMonth, dueDay, termCount, totalAmount, monthlyAmount }) {
  const rows = []
  let running = 0
  for (let i = 0; i < termCount; i++) {
    const key = shiftMonthKey(startMonth, i)
    const [y, m] = key.split('-').map(Number)
    const lastDay = new Date(y, m, 0).getDate()
    const day = Math.min(dueDay, lastDay)

    const isLast = i === termCount - 1
    const amount = isLast
      ? Math.round((totalAmount - running) * 100) / 100
      : monthlyAmount
    running += amount

    rows.push({
      seq: i + 1,
      due_date: key + '-' + String(day).padStart(2, '0'),
      amount,
      status: 'Pending',
    })
  }
  return rows
}

export async function getPlans() {
  const { data: plans, error } = await supabase
    .from('installment_plans')
    .select('*')
    .order('status')
    .order('created_at')
  if (error) throw error

  const ids = (plans ?? []).map((p) => p.id)
  let charges = []
  if (ids.length) {
    const { data, error: e2 } = await supabase
      .from('installment_charges')
      .select('*')
      .in('plan_id', ids)
      .order('seq')
    if (e2) throw e2
    charges = data ?? []
  }
  return { plans: plans ?? [], charges }
}

export async function addPlan(plan) {
  const { data, error } = await supabase
    .from('installment_plans')
    .insert({
      name: plan.name.trim(),
      merchant: plan.merchant?.trim() || null,
      card_label: plan.cardLabel?.trim() || null,
      total_amount: plan.totalAmount,
      term_count: plan.termCount,
      monthly_amount: plan.monthlyAmount,
      start_month: plan.startMonth,
      due_day: plan.dueDay,
      notes: plan.notes?.trim() || null,
    })
    .select()
    .single()
  if (error) throw error

  /*
   * Plans that started before the app did are the normal case, not the
   * exception — he is 4 of 12 into several of them. paidSoFar marks the
   * first N charges paid at creation, so the card reads 4 of 12 straight
   * away instead of showing four overdue rows to tick off one by one.
   */
  const already = Math.min(
    plan.termCount,
    Math.max(0, parseInt(plan.paidSoFar, 10) || 0),
  )

  const rows = buildSchedule({
    startMonth: plan.startMonth,
    dueDay: plan.dueDay,
    termCount: plan.termCount,
    totalAmount: plan.totalAmount,
    monthlyAmount: plan.monthlyAmount,
  }).map((r) => ({
    ...r,
    plan_id: data.id,
    status: r.seq <= already ? 'Paid' : 'Pending',
    /* Dated to when it was due rather than today, so a plan entered in
       September does not claim four payments were all made this morning. */
    paid_on: r.seq <= already ? r.due_date : null,
  }))

  const { error: e2 } = await supabase.from('installment_charges').insert(rows)
  if (e2) throw e2

  /* A plan created already complete is complete. Same rule as ticking the
     last charge by hand, applied at birth. */
  if (already >= plan.termCount) {
    await supabase.from('installment_plans').update({ status: 'Closed' }).eq('id', data.id)
    data.status = 'Closed'
  }

  await logIt(
    'plan.add',
    plan.name.trim() + ' started, ' + plan.termCount + ' months'
      + (already ? ', ' + already + ' already paid' : ''),
    data.id,
  )
  return data
}

/**
 * Ticking a charge paid. When the last one is paid the plan closes itself and
 * stops appearing in the active list — the whole point of an instalment over a
 * standing bill is that it ends.
 */
export async function setChargeStatus(plan, charge, paid, allCharges) {
  const { error } = await supabase
    .from('installment_charges')
    .update({ status: paid ? 'Paid' : 'Pending', paid_on: paid ? todayISO() : null })
    .eq('id', charge.id)
  if (error) throw error

  const others = allCharges.filter((c) => c.plan_id === plan.id && c.id !== charge.id)
  const paidCount = others.filter((c) => c.status === 'Paid').length + (paid ? 1 : 0)
  const finished = paidCount >= plan.term_count

  let closed = false
  if (finished && plan.status !== 'Closed') {
    const { error: e2 } = await supabase
      .from('installment_plans')
      .update({ status: 'Closed', closed_at: todayISO() })
      .eq('id', plan.id)
    if (e2) throw e2
    closed = true
    await logIt('plan.closed', plan.name + ' paid off', plan.id)
  } else if (!finished && plan.status === 'Closed') {
    /* Unticking the last one has to reopen it, or the plan is stuck closed
       with an unpaid charge inside it. */
    const { error: e3 } = await supabase
      .from('installment_plans')
      .update({ status: 'Active', closed_at: null })
      .eq('id', plan.id)
    if (e3) throw e3
  }

  await logIt(
    paid ? 'plan.charge.paid' : 'plan.charge.unpaid',
    plan.name + ' ' + paidCount + ' of ' + plan.term_count,
    plan.id,
  )
  return { paidCount, closed }
}

export async function deletePlan(id) {
  /* Charges cascade with the plan. */
  const { error } = await supabase.from('installment_plans').delete().eq('id', id)
  if (error) throw error
  await logIt('plan.delete', 'Plan removed', id)
}
